import { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Dot, Panel } from "@akaike/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  indiaBoundaryV1GeoIndiaBoundaryGet,
  runCoverageV1RunsRunIdCoverageGet,
} from "../api/generated/qcsControlPlane";
import { fmtInt } from "../api/labels";

// Live geographic view of a merchant sweep's scan progress. The data is binned
// *server-side* into a zoom/viewport grid (a national dense sweep is millions of
// points), so we only ever render a screen's worth of cells on a Leaflet canvas.
// We re-query as the operator pans/zooms (the server re-bins to the new window)
// and on an interval while the run is live: re-setting the canvas layer keeps the
// map's pan/zoom, unlike reloading a server-rendered <iframe>.

const INDIA: L.LatLngTuple = [22.6, 80.0];
const MIN_BIN = 0.002; // mirrors the server floor (just under the 0.005° dense step)
const CELL_PX = 10; // target on-screen cell size: drives the zoom->bin mapping

// The tight India probe-mask boundary (the polygon the sweep scans within), served
// from the control plane and drawn as a read-only outline so the operator can see the
// scanned extent. Fetched once per session and shared across every map that mounts.
let boundaryPromise: Promise<GeoJSON.GeoJsonObject> | null = null;
function loadIndiaBoundary(): Promise<GeoJSON.GeoJsonObject> {
  if (!boundaryPromise) {
    boundaryPromise = indiaBoundaryV1GeoIndiaBoundaryGet()
      .catch((err) => {
        boundaryPromise = null; // don't cache the rejection: let a later mount retry
        throw err;
      });
  }
  return boundaryPromise;
}

// Grid cell size (degrees) for a zoom level: ~CELL_PX pixels per cell. Depends only
// on the integer zoom, so cells stay aligned across polls while panning.
function binForZoom(zoom: number): number {
  const degPerPx = 360 / (256 * 2 ** zoom);
  return Math.max(MIN_BIN, CELL_PX * degPerPx);
}

function round(n: number, dp = 4): number {
  const f = 10 ** dp;
  return Math.round(n * f) / f;
}

// Serviceable fraction -> hue from red (none) through amber to green (all serviceable).
function binColor(frac: number): string {
  const f = Math.max(0, Math.min(1, frac));
  return `hsl(${(8 + 132 * f).toFixed(0)}, 68%, 46%)`;
}

type View = { minLat: number; minLon: number; maxLat: number; maxLon: number; bin: number };

export function RunCoverageMap({ runId, live }: { runId: string; live: boolean }) {
  const elRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const binsRef = useRef<L.LayerGroup | null>(null);
  const storesRef = useRef<L.LayerGroup | null>(null);
  const fittedRef = useRef(false);
  const [view, setView] = useState<View | null>(null);

  // Viewport -> query string. Omitted on first paint so the server returns a coarse
  // grid over the whole data extent (which we then fit-to-bounds and refine).
  const qs = useMemo(() => {
    if (!view) return "";
    return `?${new URLSearchParams({
      min_lat: String(view.minLat),
      min_lon: String(view.minLon),
      max_lat: String(view.maxLat),
      max_lon: String(view.maxLon),
      bin: String(view.bin),
    })}`;
  }, [view]);

  const coverage = useQuery({
    queryKey: ["run-coverage", runId, qs],
    queryFn: () =>
      runCoverageV1RunsRunIdCoverageGet(
        runId,
        view
          ? {
              min_lat: view.minLat,
              min_lon: view.minLon,
              max_lat: view.maxLat,
              max_lon: view.maxLon,
              bin: view.bin,
            }
          : undefined,
      ),
    refetchInterval: live ? 4000 : false,
    placeholderData: keepPreviousData, // don't blank the map between viewport changes
  });

  // -- create the map once ------------------------------------------------------
  useEffect(() => {
    if (!elRef.current || mapRef.current) return;
    const map = L.map(elRef.current, {
      attributionControl: false,
      preferCanvas: true,
      worldCopyJump: true,
    }).setView(INDIA, 5);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(map);
    binsRef.current = L.layerGroup().addTo(map);
    storesRef.current = L.layerGroup().addTo(map);

    // Read-only India boundary outline (decorative; a failure must not break the map).
    loadIndiaBoundary()
      .then((gj) => {
        if (!mapRef.current) return; // unmounted before the fetch resolved
        L.geoJSON(gj, {
          interactive: false,
          style: { color: "#8aa0b6", weight: 1, opacity: 0.6, fill: false },
        }).addTo(map);
      })
      .catch(() => {});

    // Re-query when a pan/zoom gesture ends, with the new window + zoom-derived bin.
    const sync = () => {
      const b = map.getBounds().pad(0.25);
      setView({
        minLat: round(b.getSouth()),
        minLon: round(b.getWest()),
        maxLat: round(b.getNorth()),
        maxLon: round(b.getEast()),
        bin: round(binForZoom(map.getZoom()), 6),
      });
    };
    map.on("moveend", sync);
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 0); // settle size once the panel has laid out
    return () => {
      map.remove();
      mapRef.current = null;
      fittedRef.current = false;
    };
  }, []);

  // -- redraw the bin + store layers whenever the data changes ------------------
  useEffect(() => {
    const map = mapRef.current;
    const bins = binsRef.current;
    const stores = storesRef.current;
    const data = coverage.data;
    if (!map || !bins || !stores || !data) return;

    // Fit to the data extent the first time we see it, then leave the view alone.
    if (!fittedRef.current && data.extent) {
      const [s, w, n, e] = data.extent;
      fittedRef.current = true;
      if (n > s && e > w) map.fitBounds([[s, w], [n, e]], { padding: [24, 24], maxZoom: 13 });
      else map.setView([s, w], 12);
    }

    const half = (data.bin_size ?? MIN_BIN) / 2;
    bins.clearLayers();
    for (const [lat, lon, count, serv] of data.bins) {
      L.rectangle(
        [
          [lat - half, lon - half],
          [lat + half, lon + half],
        ],
        {
          stroke: false,
          fill: true,
          fillColor: binColor(count > 0 ? serv / count : 0),
          fillOpacity: 0.55,
          interactive: false,
        },
      ).addTo(bins);
    }

    stores.clearLayers();
    for (const st of data.stores) {
      const eta = st.eta_minutes != null ? ` · ${st.eta_minutes}m` : "";
      const name = st.name ? `<br/>${st.name}` : "";
      L.circleMarker([st.latitude, st.longitude], {
        radius: 5,
        color: "#1f6feb",
        weight: 1.5,
        fillColor: "#1f6feb",
        fillOpacity: 0.9,
      })
        .bindPopup(`<b>${st.store_id}</b>${eta}${name}`)
        .addTo(stores);
    }
  }, [coverage.data]);

  // Capture the finalize flush: refetch once when the run goes terminal.
  useEffect(() => {
    if (!live) coverage.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live]);

  const c = coverage.data?.counts;
  const probed = c?.probed ?? 0;
  return (
    <Panel style={{ gridColumn: "span 12" }}>
      <Panel.Head title="Coverage map">
        {live && (
          <Badge variant="outline-success" style={{ marginLeft: 8, fontSize: 10.5 }}>
            <Dot live /> live
          </Badge>
        )}
        <div className="flex-1" />
        {c && (
          <span className="num faint" style={{ fontSize: 11 }}>
            {fmtInt(c.probed)} probed · {fmtInt(c.serviceable)} serviceable · {fmtInt(c.stores)} stores
          </span>
        )}
      </Panel.Head>
      <Panel.Body tight style={{ position: "relative" }}>
        <div
          ref={elRef}
          style={{ width: "100%", height: 460, borderRadius: 6, overflow: "hidden", zIndex: 0 }}
        />
        {coverage.isError ? (
          <Overlay>Could not load coverage (is ClickHouse configured?).</Overlay>
        ) : probed === 0 ? (
          <Overlay>{live ? "Waiting for the first probes…" : "No coverage recorded for this run."}</Overlay>
        ) : null}
        <Legend capped={coverage.data?.capped ?? false} />
      </Panel.Body>
    </Panel>
  );
}

// Color/marker key + the "zoom in for detail" hint when the grid hit the cell cap.
function Legend({ capped }: { capped: boolean }) {
  const swatch = (color: string, label: string) => (
    <span className="row" style={{ gap: 5, alignItems: "center" }}>
      <span style={{ width: 11, height: 11, borderRadius: 2, background: color, display: "inline-block" }} />
      <span className="faint" style={{ fontSize: 10 }}>{label}</span>
    </span>
  );
  return (
    <div
      className="col"
      style={{
        position: "absolute",
        left: 12,
        bottom: 14,
        gap: 5,
        padding: "8px 10px",
        background: "var(--panel, rgba(20,24,33,0.86))",
        border: "1px solid var(--line)",
        borderRadius: 6,
        zIndex: 500,
        pointerEvents: "none",
      }}
    >
      {swatch(binColor(1), "serviceable")}
      {swatch(binColor(0.5), "mixed cell")}
      {swatch(binColor(0), "not serviceable")}
      {swatch("#1f6feb", "discovered store")}
      {capped && (
        <span className="faint" style={{ fontSize: 9.5, maxWidth: 150, lineHeight: 1.4 }}>
          grid capped: zoom in for finer detail
        </span>
      )}
    </div>
  );
}

function Overlay({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="muted"
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 12.5,
        pointerEvents: "none",
        background: "rgba(0,0,0,0.04)",
        zIndex: 400,
      }}
    >
      {children}
    </div>
  );
}
